import { z } from 'zod';
import { DEPARTMENT_DEFS } from './hr.js';
import { MAX_DUTIES, SUCCESSION_READINESS } from './orgdev.js';

/** مخططات التطوير التنظيمي. */

const Cuid = z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.');

const DEPARTMENT_KEYS_TUPLE = DEPARTMENT_DEFS.map((d) => d.key) as unknown as [
  string,
  ...string[],
];

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const money = z.coerce.number().min(0).max(99_999_999_999.99);

/* ==========================================================================
   الدرجات
   ========================================================================== */

/**
 * درجةٌ بنطاقها.
 *
 * الترتيبُ يتحقّق هنا لا في الشاشة: نطاقٌ أدناه أعلى من أعلاه يجعل كلَّ
 * راتبٍ يبدو خارج نطاقه، ولا يُكتشف الخطأ إلّا في مراجعة رواتبٍ كاملة.
 */
export const UpsertGradeSchema = z
  .object({
    code: z
      .string()
      .trim()
      .min(1, 'كود الدرجة مطلوب.')
      .max(20, 'كود الدرجة أطول من عشرين حرفًا.'),
    name: z.string().trim().min(2, 'اسم الدرجة قصير.').max(120),
    level: z.coerce
      .number()
      .int('المستوى رقم صحيح.')
      .min(1, 'المستوى واحدٌ فأعلى.')
      .max(99),
    minSalary: money.nullable().optional(),
    midSalary: money.nullable().optional(),
    maxSalary: money.nullable().optional(),
    notes: optionalText(1000),
    isActive: z.boolean().default(true),
  })
  .refine(
    (v) =>
      v.minSalary == null || v.maxSalary == null || v.minSalary <= v.maxSalary,
    { message: 'أدنى النطاق أعلى من أقصاه.', path: ['maxSalary'] },
  )
  .refine(
    (v) =>
      v.midSalary == null ||
      ((v.minSalary == null || v.midSalary >= v.minSalary) &&
        (v.maxSalary == null || v.midSalary <= v.maxSalary)),
    { message: 'الوسط خارج النطاق.', path: ['midSalary'] },
  );
export type UpsertGradeInput = z.infer<typeof UpsertGradeSchema>;

/* ==========================================================================
   الأوصاف الوظيفية
   ========================================================================== */

const bullets = z
  .array(z.string().trim().min(1).max(300))
  .max(MAX_DUTIES, `العناصر أكثر من ${MAX_DUTIES}.`)
  .default([]);

export const UpsertPositionSchema = z.object({
  code: optionalText(30),
  title: z.string().trim().min(2, 'المسمّى قصير.').max(160),
  deptKey: z.enum(DEPARTMENT_KEYS_TUPLE).nullable().optional(),
  gradeId: Cuid.nullable().optional(),
  /** الوحدةُ التي تقع فيها — تُرسم في الهيكل التنظيميّ. */
  orgUnitId: Cuid.nullable().optional(),
  purpose: optionalText(2000),
  duties: bullets,
  requirements: bullets,
  reportsToId: Cuid.nullable().optional(),
  nextPositionId: Cuid.nullable().optional(),
  headcount: z.coerce
    .number()
    .int('العدد المعتمَد رقم صحيح.')
    .min(0, 'العدد المعتمَد صفرٌ فأعلى.')
    .max(999)
    .default(1),
  isActive: z.boolean().default(true),
});
export type UpsertPositionInput = z.infer<typeof UpsertPositionSchema>;

export const PositionsQuerySchema = z.object({
  deptKey: z.string().trim().max(40).optional(),
  gradeId: Cuid.optional(),
  q: z.string().trim().max(120).optional(),
  /** الموقوفةُ تُخفى افتراضًا — تُطلب صراحةً. */
  all: z.enum(['1']).optional(),
});
export type PositionsQuery = z.infer<typeof PositionsQuerySchema>;

/* ==========================================================================
   الإحلال
   ========================================================================== */

/**
 * مرشّحٌ لخلافة وظيفة.
 *
 * الفجواتُ تُكتب ولا تُترك: «جاهزٌ خلال سنة» بلا ذكرِ ما ينقصه جملةٌ لا
 * يُبنى عليها تدريب، وهي بالضبط ما يُفترض أن يُنتجه هذا التخطيط.
 */
export const UpsertSuccessorSchema = z
  .object({
    employeeId: Cuid,
    readiness: z.enum(SUCCESSION_READINESS).default('SOON'),
    notes: optionalText(2000),
    gaps: bullets,
  })
  .refine((v) => v.readiness === 'NOW' || v.gaps.length > 0 || !!v.notes, {
    message: 'من ليس جاهزًا الآن يُكتب ما ينقصه.',
    path: ['gaps'],
  });
export type UpsertSuccessorInput = z.infer<typeof UpsertSuccessorSchema>;
